import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { getTranscriptAround, type TranscriptBlock, type TranscriptSource } from '../../transport/api'
import { ApiError } from '../../transport/http'
import { Icon } from '../../ui/Icon'
import { clockLabel } from '../blockTime'
import { Msg, Turn } from '../transcriptBlocks'
import { groupTurns } from '../turns'
import { BookmarkContext, type BookmarkCtx } from './BookmarkButton'
import type { NewBookmark } from './useBookmarks'
import type { JumpResult } from './jump'

/**
 * AN EARLIER PART OF THE CONVERSATION, OPENED AT A BOOKMARK — owner, 2026-11-04: Bookmarks 裡面的內容被壓縮之後, 點擊就找不到
 * 了. 得想個辦法看看怎麼樣比較好的解決這件事情.
 *
 * The Clean View holds the newest blocks the agent's machine keeps (2,000) and pages back only through them. A long
 * conversation — the kind that has been compacted a few times — leaves its older bookmarks below that, and pressing one
 * said so in a status line and went nowhere. Now the machine reads that part of the conversation from the file
 * (/agent/transcript?around=, in a process of its own) and it opens here, over the newest messages: the bookmarked one in
 * the middle and lit, drawn exactly as the conversation draws it, with more above and below a tap away and "Back to
 * latest" to return. The newest messages carry on underneath, untouched.
 */
const SPAN = 25

type Part = { from: number; blocks: TranscriptBlock[]; total: number }

export function EarlierPart({
  session,
  hash,
  host,
  device,
  source,
  file,
  index,
  liveFrom,
  resolve,
  describeHash,
  describeRoom,
  ownerName,
  resolutionKey,
  bm,
  onResult,
  onClose,
}: {
  session: string
  hash: string
  host: string | null
  device: string | null
  source: TranscriptSource
  /** The conversation the bookmark is in (and the Clean View is showing). */
  file: string | null
  /** The bookmarked block. */
  index: number
  /** Where the newest messages the view holds begin: past it, "Back to latest" instead of "Show later". */
  liveFrom: number
  resolve: (label: string) => string | null
  describeHash?: (h: string) => { name: string; machine: string } | null
  describeRoom?: (id: string) => string | null
  ownerName: string | null
  resolutionKey?: string
  /** The bookmarks of this conversation, so the buttons under these messages work as they do below. */
  bm: { supported: boolean; keptAt: (key: string) => string | null; add: (b: NewBookmark) => void; remove: (id: string) => void }
  /** How the opening went: 'shown', or why not ('elsewhere' — another conversation; 'unreachable' — anything else). */
  onResult: (r: JumpResult) => void
  onClose: () => void
}) {
  const [part, setPart] = useState<Part | null>(null)
  const [busy, setBusy] = useState<'up' | 'down' | null>(null)
  const [moreError, setMoreError] = useState(false)
  const body = useRef<HTMLDivElement>(null)
  /** Scroll height minus scroll top before older blocks went in above — restored after, so the reader stays put. */
  const anchor = useRef<number | null>(null)
  const lit = useRef(false)
  const result = useRef(onResult)
  result.current = onResult

  // The part around the bookmark, once.
  useEffect(() => {
    let live = true
    getTranscriptAround(session, index, SPAN, host, source, file, device)
      .then((t) => {
        if (!live) return
        // An edge from before answers with an ordinary window: it cannot read that far back.
        if (t.mode !== 'range' || !Array.isArray(t.blocks) || !t.blocks.length) {
          result.current('unreachable')
          return
        }
        setPart({ from: t.from, blocks: t.blocks, total: t.total })
      })
      .catch((e: unknown) => {
        if (!live) return
        result.current(e instanceof ApiError && e.status === 409 ? 'elsewhere' : 'unreachable')
      })
    return () => {
      live = false
    }
  }, [session, index, host, source, file, device])

  // Opened: the bookmarked message to the middle, lit — the same light a jump in the newest messages gives.
  useLayoutEffect(() => {
    const el = body.current
    if (!el || !part) return
    if (anchor.current !== null) {
      el.scrollTop = el.scrollHeight - anchor.current
      anchor.current = null
      return
    }
    if (lit.current) return
    lit.current = true
    const marks = [...el.querySelectorAll<HTMLElement>('[data-bm-index]')]
    const at = (e: HTMLElement) => Number(e.dataset['bmIndex'])
    // The message whose button holds that index — or, inside a closed fold, the turn's own message after it.
    const mark = marks.find((e) => at(e) === index) ?? marks.filter((e) => at(e) >= index).sort((a, b) => at(a) - at(b))[0]
    const msg = (mark?.closest('.turn-reply, .pmsg') ?? null) as HTMLElement | null
    if (msg) {
      msg.scrollIntoView?.({ block: 'center' })
      msg.classList.add('bm-flash')
      msg.addEventListener('animationend', () => msg.classList.remove('bm-flash'), { once: true })
    }
    result.current('shown')
  }, [part, index])

  /** More of the conversation: above what is here (`up`) or below it. Only the run that joins what is here is taken. */
  const more = (dir: 'up' | 'down') => {
    if (!part || busy) return
    const end = part.from + part.blocks.length
    const around = dir === 'up' ? part.from - 1 : end
    setBusy(dir)
    setMoreError(false)
    getTranscriptAround(session, around, SPAN, host, source, file, device)
      .then((t) => {
        if (t.mode !== 'range' || !Array.isArray(t.blocks)) throw new Error('not a range')
        const got = t.blocks.map((b, i) => ({ b, i: t.from + i }))
        if (dir === 'up') {
          const add = got.filter((x) => x.i < part.from)
          if (!add.length) return
          const el = body.current
          if (el) anchor.current = el.scrollHeight - el.scrollTop
          setPart({ from: add[0]!.i, blocks: [...add.map((x) => x.b), ...part.blocks], total: t.total || part.total })
        } else {
          const add = got.filter((x) => x.i >= end)
          setPart({ from: part.from, blocks: [...part.blocks, ...add.map((x) => x.b)], total: t.total || part.total })
        }
      })
      .catch(() => setMoreError(true))
      .finally(() => setBusy(null))
  }

  const addresses = useMemo(() => new Map((part?.blocks ?? []).map((b, i) => [b, part!.from + i])), [part])
  const items = useMemo(() => groupTurns(part?.blocks ?? []), [part])
  const { supported, keptAt, add, remove } = bm
  const ctx = useMemo<BookmarkCtx | null>(
    () =>
      supported && file
        ? {
            addressOf: (b) => {
              const i = addresses.get(b)
              return i === undefined ? null : { key: `${file}:${i}`, file, index: i }
            },
            keptAt,
            add,
            remove,
            ready: true,
          }
        : null,
    [supported, file, addresses, keptAt, add, remove],
  )

  const target = part?.blocks[index - part.from]
  const end = part ? part.from + part.blocks.length : 0
  const noHits = useMemo(() => new Set<TranscriptBlock>(), [])
  return (
    <section className="ep" aria-label="An earlier part of this conversation">
      <div className="ep-bar">
        <Icon name="history" size={16} />
        <span className="ep-title">
          Earlier in this conversation
          {target?.ts ? <span className="ep-when"> · {clockLabel(target.ts)}</span> : null}
        </span>
        <button type="button" className="ep-back" onClick={onClose}>
          Back to latest
        </button>
      </div>
      <div className="ep-body" ref={body}>
        {!part && <div className="pm-empty">Opening that part of the conversation…</div>}
        {part && part.from > 0 && (
          <button type="button" className="pm-empty pm-loadmore" disabled={busy !== null} onClick={() => more('up')}>
            {busy === 'up' ? 'Loading…' : 'Show earlier messages'}
          </button>
        )}
        <BookmarkContext.Provider value={ctx}>
          {items.map((item) =>
            item.kind === 'said' ? (
              <Msg
                key={`s${addresses.get(item.block)}`}
                b={item.block}
                session={session}
                hash={hash}
                resolve={resolve}
                {...(describeHash ? { describeHash } : {})}
                {...(describeRoom ? { describeRoom } : {})}
                ownerName={ownerName}
                {...(resolutionKey !== undefined ? { resolutionKey } : {})}
              />
            ) : (
              <Turn
                key={`t${addresses.get(item.blocks[0]!)}`}
                blocks={item.blocks}
                session={session}
                hash={hash}
                resolve={resolve}
                {...(describeHash ? { describeHash } : {})}
                {...(describeRoom ? { describeRoom } : {})}
                ownerName={ownerName}
                {...(resolutionKey !== undefined ? { resolutionKey } : {})}
                hits={noHits}
              />
            ),
          )}
        </BookmarkContext.Provider>
        {moreError && <div className="pm-empty sb-err">That part could not be read just now. Try again in a moment.</div>}
        {part &&
          (end < liveFrom ? (
            <button type="button" className="pm-empty pm-loadmore" disabled={busy !== null} onClick={() => more('down')}>
              {busy === 'down' ? 'Loading…' : 'Show later messages'}
            </button>
          ) : (
            <button type="button" className="pm-empty pm-loadmore" onClick={onClose}>
              Back to latest
            </button>
          ))}
      </div>
    </section>
  )
}
